/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { registerCoreCommands } from "./Commands";
import { IframeView } from "./IframeView/IframeView";
import { IframeViewComponent } from "./IframeView/IframeViewComponent";
import { registerCoreKeyBindings } from "./KeyBindings";
import { registerCoreMenus } from "./Menus";
import { ErrorLogView } from "./ErrorLogView/ErrorLogView";
import { ErrorLogViewComponent } from "./ErrorLogView/ErrorLogViewComponent";
import { AuthenticateDialog } from "./AuthenticateDialog/AuthenticateDialog";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import { TOAST_TYPE } from "../../toasts/IToast";
import { Editor } from "../../Editor";
import {
	defaultPreviewResolutionSettings,
	PREVIEW_RESOLUTION_SETTINGS_STORAGE_KEY
} from "../../previewResolution";
import { RoutesEditor } from "./RoutesEditor/RoutesEditor";
import { RoutesEditorComponent } from "./RoutesEditor/RoutesEditorComponent";
import { RESOURCE_TYPES } from "@adapptio/lib-core";
import { ROUTES_EDITOR_VIEWS } from "./RoutesEditor/RoutesEditorTypes";
import { ResourceDebugView } from "./ResourceDebugView/ResourceDebugView";
import { ResourceDebugViewComponent } from "./ResourceDebugView/ResourceDebugViewComponent";
import { ReferenceGuide } from "./ReferenceGuide/ReferenceGuide";
import { ReferenceGuideComponent } from "./ReferenceGuide/ReferenceGuideComponent";
import { APP_MENU_STATE_DEFAULT, APP_MENU_STATE_STORAGE_KEY } from "../../ui/components/AppMenu";

/**
 * Core modules
 * Is registered automatically.
 *
 * @param dc Dependency Container
 */
export function CoreModule(dc: Editor): void {
	dc.get("logger").debug("Core module enabled.");

	const logger = dc.get("logger");
	const apiSvc = dc.get("apiService");
	const viewSvc = dc.get("viewService");
	const dialogSvc = dc.get("dialogService");
	const resourceSvc = dc.get("resourceService");
	const toastSvc = dc.get("toastService");
	const stateService = dc.get("stateService");

	viewSvc.registerView("iframe", IframeView, IframeViewComponent);
	viewSvc.registerView("errorLog", ErrorLogView, ErrorLogViewComponent);
	viewSvc.registerView(ROUTES_EDITOR_VIEWS.EDIT, RoutesEditor, RoutesEditorComponent);
	viewSvc.registerView("debug", ResourceDebugView, ResourceDebugViewComponent);
	viewSvc.registerView("reference-guide", ReferenceGuide, ReferenceGuideComponent);

	resourceSvc.registerFallbackResourceView("debug");

	resourceSvc.registerResourceView(RESOURCE_TYPES.ROUTES, ROUTES_EDITOR_VIEWS.EDIT);

	registerCoreCommands(dc);
	registerCoreMenus(dc);
	registerCoreKeyBindings(dc);

	// Handle unauthorized event
	let hasBeenUnauthorized = false;

	resourceSvc.registerResourceCreateCommand(RESOURCE_TYPES.DIRECTORY, "resource.create.directory");

	const handleUnauthorized = () => {
		if (hasBeenUnauthorized) {
			return;
		}

		hasBeenUnauthorized = true;

		logger.info("Session is not unauthorized anymore.");

		dialogSvc.open(AuthenticateDialog, "unauthorizedMessage", {});

		// Setup timer to close dialog if login has been restored
		const check = setInterval(() => {
			apiSvc
				.loadEditorSession()
				.then(() => {
					logger.info("Session authorization has been restored.");
				})
				.catch(() => {
					logger.info("Session is still unauthorized, will re-check.");
				});
		}, 3000);

		const handleAuth = () => {
			hasBeenUnauthorized = false;

			clearInterval(check);
			offEvent(apiSvc.onAuthorize, handleAuth);

			dialogSvc.getDialogById("unauthorizedMessage").close({});
		};

		onEvent(apiSvc.onAuthorize, handleAuth);
	};

	// Handle online/offline event
	let lastOfflineToastId;

	const handleOnline = () => {
		if (lastOfflineToastId) {
			toastSvc.remove(lastOfflineToastId);
			lastOfflineToastId = null;
		}

		toastSvc.add({
			type: TOAST_TYPE.SUCCESS,
			icon: "mdi/check-network",
			message: "You are back on-line",
			timeoutMs: 2000,
			isClosable: true
		});
	};

	const handleOffline = () => {
		lastOfflineToastId = toastSvc.add({
			type: TOAST_TYPE.ERROR,
			message: "You are offline",
			details:
				"Your internet connection is not available. Editor will not work properly until you connect again.",
			isClosable: true
		});
	};

	if (!stateService.has(PREVIEW_RESOLUTION_SETTINGS_STORAGE_KEY)) {
		stateService.register(PREVIEW_RESOLUTION_SETTINGS_STORAGE_KEY, defaultPreviewResolutionSettings);
	}

	if (!stateService.has(APP_MENU_STATE_STORAGE_KEY)) {
		stateService.register(APP_MENU_STATE_STORAGE_KEY, APP_MENU_STATE_DEFAULT);
	}

	onEvent(dc.onInitStateChange, () => {
		onEvent(apiSvc.onUnauthorized, handleUnauthorized);

		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
	});

	onEvent(dc.onTeardown, () => {
		offEvent(apiSvc.onUnauthorized, handleUnauthorized);

		window.removeEventListener("online", handleOnline);
		window.removeEventListener("offline", handleOffline);
	});
}
